import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PessoaDataService } from '@nomoh-monorepo/data-access-shared-services';
import { IPessoa, IPlano } from '@nomoh-monorepo/shared-types';
import {
  formatarCEP,
  formatarCNPJ,
  formatarCPF,
  formatarTelefone,
} from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { PessoaPermissionsExtrasTabComponent } from './pessoa-permissions-extras-tab/pessoa-permissions-extras-tab.component';

@Component({
  selector: 'lib-pessoa-detalhes-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TabViewModule,
    ButtonModule,
    TableModule,
    TagModule,
    AvatarModule,
    ProgressSpinnerModule,
    TooltipModule,
    DropdownModule,
    TranslateModule,
    PessoaPermissionsExtrasTabComponent,
  ],
  templateUrl: './pessoa-detalhes-modal.component.html',
  styleUrls: ['./pessoa-detalhes-modal.component.scss'],
})
export class PessoaDetalhesModalComponent implements OnInit, OnChanges {
  // ===== INPUTS =====
  @Input() pessoaId!: string;
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  // ✅ NOVOS INPUTS: Configuração do comportamento
  @Input() showPermissionsTab = false; // Exibir tab de permissões extras?
  @Input() canEditPermissions = false; // Pode editar permissões?

  // ===== SIGNALS =====
  private _pessoa = signal<IPessoa | null>(null);
  private _planos = signal<IPlano[]>([]);
  private _planosDisponiveis = signal<IPlano[]>([]);
  private _loading = signal<boolean>(false);
  private _mostrarModalVincular = signal<boolean>(false);
  private _planoSelecionado = signal<string | null>(null);

  $pessoa = this._pessoa.asReadonly();
  $planos = this._planos.asReadonly();
  $planosDisponiveis = this._planosDisponiveis.asReadonly();
  $loading = this._loading.asReadonly();
  $mostrarModalVincular = this._mostrarModalVincular.asReadonly();
  $planoSelecionado = this._planoSelecionado.asReadonly();

  translate = inject(TranslateService);

  // Computed para URL da imagem (formato: data:image/png;base64,...)
  $imagemUrl = computed(() => {
    const pessoa = this.$pessoa();
    if (pessoa?.imagemPerfil && pessoa?.typeImage) {
      return `data:${pessoa.typeImage};base64,${pessoa.imagemPerfil}`;
    }
    return null;
  });

  // ===== SERVICES =====
  private pessoaDataService = inject(PessoaDataService);

  ngOnInit(): void {
    if (this.pessoaId) {
      this.carregarDados();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recarregar dados quando pessoaId mudar
    if (changes['pessoaId'] && !changes['pessoaId'].firstChange) {
      if (this.pessoaId) {
        this.carregarDados();
      }
    }
  }

  async carregarDados(): Promise<void> {
    this._loading.set(true);
    try {
      // Buscar pessoa completa (incluindo endereço)
      const pessoa = await this.pessoaDataService.buscarPorId(this.pessoaId);
      this._pessoa.set(pessoa);

      // Buscar planos associados (retorna IPlano[] diretamente)
      const planos = await this.pessoaDataService.buscarPlanos(this.pessoaId);
      this._planos.set(planos);
    } finally {
      this._loading.set(false);
    }
  }

  async removerPlano(planoId: string): Promise<void> {
    if (!this.pessoaId) return;

    const sucesso = await this.pessoaDataService.desvincularPlano(
      this.pessoaId,
      planoId
    );

    if (sucesso) {
      // Recarregar planos após remoção
      const planos = await this.pessoaDataService.buscarPlanos(this.pessoaId);
      this._planos.set(planos);
    }
  }

  async abrirModalVincularPlano(): Promise<void> {
    // Carregar todos os planos disponíveis
    const todosPlanos = await this.pessoaDataService.buscarTodosPlanos();
    this._planosDisponiveis.set(todosPlanos);
    this._mostrarModalVincular.set(true);
  }

  fecharModalVincular(state: boolean): void {
    this._mostrarModalVincular.set(state);
    if (!state) {
      this._planoSelecionado.set(null);
    }
  }

  async vincularPlano(): Promise<void> {
    const planoId = this._planoSelecionado();
    if (!this.pessoaId || !planoId) return;

    // Buscar planos atuais do usuário
    const planosUsuario = this.$planos();

    // Buscar info do plano novo
    const planoNovo = this.$planosDisponiveis().find((p) => p.id === planoId);
    if (!planoNovo) return;

    // Verificar se já tem plano do mesmo sistema
    const planoExistenteDoSistema = planosUsuario.find(
      (p) => p.sistemaId === planoNovo.sistemaId
    );

    const sucesso = await this.pessoaDataService.vincularPlano(
      this.pessoaId,
      planoId,
      !!planoExistenteDoSistema
    );

    if (sucesso) {
      // Recarregar planos após vinculação
      const planos = await this.pessoaDataService.buscarPlanos(this.pessoaId);
      this._planos.set(planos);
      this.fecharModalVincular(false);
    }
  }

  onPlanoSelecionadoChange(planoId: string): void {
    this._planoSelecionado.set(planoId);
  }

  verificarSubstituicao(planoId: string): boolean {
    const planosUsuario = this.$planos();
    const planoNovo = this.$planosDisponiveis().find((p) => p.id === planoId);

    if (!planoNovo) return false;

    // Verifica se já existe plano do mesmo sistema
    return planosUsuario.some((p) => p.sistemaId === planoNovo.sistemaId);
  }

  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  formatarTelefone(telefone: string | null | undefined): string {
    return telefone ? formatarTelefone(telefone) : '';
  }
  formatarCPFOUCNPJ(cpfcnpj: string): string {
    if (!cpfcnpj) {
      return '';
    }

    if (cpfcnpj.length === 11) {
      return formatarCPF(cpfcnpj);
    } else {
      return formatarCNPJ(cpfcnpj);
    }
  }

  formatarCEP(cep: string | null | undefined): string {
    return cep ? formatarCEP(cep) : '';
  }

  formatarData(dataISO: string): string {
    // CRÍTICO: Auto-detecta região da máquina do usuário para sistema internacional
    return DateTime.fromISO(dataISO)
      .toLocal()
      .toLocaleString(DateTime.DATE_SHORT);
  }

  get dadosPessoaisHeader(): string {
    return this.translate.instant('PESSOAS_MODAL_DETALHE.DADOS_PESSOAIS');
  }

  get planosHeader(): string {
    return this.translate.instant('PESSOAS_MODAL_DETALHE.PLANOS');
  }

  get permissoesExtrasHeader(): string {
    return this.translate.instant('PESSOAS_MODAL_DETALHE.PERMISSOES_EXTRAS');
  }

  get tituloHeader(): string {
    return this.translate.instant('PESSOAS_MODAL_DETALHE.TITULO');
  }
}
